import { Component, OnInit } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { selectAuthLoaded } from '../core/store/selectors/authentication.selectors';
import { selectInternetOnline } from '../core/store/selectors/internet.selectors';
import { Action_Internet_Init } from '../core/store/actions/internet.actions';
import { filter, map, tap } from 'rxjs/operators';
import { ConsoleService } from '../core/services/console.service';
import { SwUpdate } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { combineLatest, interval } from 'rxjs';
import { UtilService } from '../core/services/util.service';
import { WarningLog } from 'src/assets/helpers/console-log';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BrowserPreference } from '../core/services/browser-preferences.service';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { Action_Session_Init } from '../core/store/actions/session.actions';


@Component({
  selector: 'gio-root',
  templateUrl: './boot.component.html',
  styleUrls: ['./boot.component.scss']
})
export class BootComponent implements OnInit {

  isLoaded$ = this.store.select(selectAuthLoaded);
  navigation$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd));
  internetStatus$ = this.store.select(selectInternetOnline);

  isConnected$ = combineLatest([this.internetStatus$, this.navigation$]).pipe(map(([connection, _]) => {
    if (environment.environment == 'local') {
      return true;
    }

    // lookup for hideOfflineWarning on childRoutes
    let child = this.actRoute.firstChild;
    while (child) {
      if (child.snapshot.data.hideOfflineWarning) {
        child = null;
        return true;
      }
      child = child.firstChild;
    }
    return connection;
  }));

  loading = true;
  routing = true;
  showCookieDialog = false;

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private sConsole: ConsoleService,
    private updates: SwUpdate,
    private util: UtilService,
    private analytics: Analytics,
    private actRoute: ActivatedRoute,
    private router: Router
  ) { }

  findHideOfflineWarning(route: ActivatedRoute): ActivatedRouteSnapshot {
    if (route.snapshot.data?.hideOfflineWarning) {
      return route.snapshot;
    }

    if (route.children && route.children.length > 0) {
      for (let i = 0; i <= route.children.length - 1; i++) {
        const found = this.findHideOfflineWarning(route.children[i]);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  ngOnInit() {

    this.verifyCookieAcceptance();

    this.subscribeUpdates();
    this.sConsole.init();
    this.store.dispatch(new Action_Internet_Init());
    this.store.dispatch(new Action_Session_Init());
    this.actions$.pipe(tap((action) => this.routeStateChanged(action))).subscribe();
    // tslint:disable-next-line: no-unused-expression
    new WarningLog();

    logEvent(this.analytics, 'session_start');


    document.onkeydown = (event) => {
      switch (event.code) {
        case 'F5': {
          return false;
        }
        case 'KeyR': {
          if (event.ctrlKey) {
            return false;
          }
        }
      }
    }

  }

  async verifyCookieAcceptance() {
    const accepted = await this.util.preferences.get(BrowserPreference.COOKIE_ACCEPTED);
    if (!accepted) {
      this.showCookieDialog = true;
    }
  }

  routeStateChanged(action: Action) {
    if (action.type.startsWith('@ngrx/router-store/navigated')) {
      this.routing = false;
    }

    if (action.type.startsWith('@ngrx/router')) {
      const isLoading = ['@ngrx/router-store/request', '@ngrx/router-store/navigation'];
      setTimeout(() => {
        this.loading = isLoading.includes(action.type);
      }, 10);
    }
  }

  // verify when a new angular version is available
  subscribeUpdates() {

    if (['staging', 'production'].includes(environment.environment)) {

      // log versions

      // Allow the app to stabilize first, before starting polling for updates with `interval()`.
      let timer = 1000 * 60;
      if (environment.environment == 'staging') {
        timer = 1000 * 60;
      }
      const everyTenMinutes$ = interval(timer);

      everyTenMinutes$.subscribe(() => {
        this.updates.checkForUpdate();
      });


      this.updates.available.subscribe(event => {
        this.util.dialog.confirm(`Uma nova versão do sistema foi lançada. Vamos atualizar agora?`,
          'Nova Versão!', 'Atualização Necessária', false)
          .then((confirm) => {
            if (confirm) {
              this.updates.activateUpdate().then(() => document.location.reload());
            } else {
              console.error('Update rejected by user action');
            }
          });
      });
    }


  }

}
